import React from "react";
import { Input, Skeleton, Typography } from "antd";
import { AimOutlined } from "@ant-design/icons";
import { Container, DropDownContainer, ResultItem } from "./elements";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
  Suggestion,
} from "react-places-autocomplete";

const { Text } = Typography;

// Define search options for Google Places API
const searchOptions = {
  componentRestrictions: { country: ["mx"] },
};

// Define types for component props
interface LocationSearchInputProps {
  value: string;
  onChange: (address: string) => void;
  onSelect: (data: {
    result: string;
    address: google.maps.GeocoderResult[];
    lat: number;
    lng: number;
  }) => void;
  placeholder?: string;
}

// TypeScript version of the component
const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter a location...",
  ...props
}) => {
  const handleSelect = async (result: string) => {
    const address = await geocodeByAddress(result);
    const latLng = await getLatLng(address[0]);

    // Google Address Formatter
    const googleFormattedAddress: Record<string, string> = {};
    const addressComponentsMap: Record<string, string> = {
      country: "country",
      administrative_area_level_1: "state",
      locality: "city",
      sublocality: "colony",
      neighborhood: "neighborhood",
      route: "street",
      postal_code: "postcode",
    };

    if (!address[0]) return;
    for (const component of address[0].address_components) {
      for (const type of component.types) {
        if (addressComponentsMap[type]) {
          googleFormattedAddress[addressComponentsMap[type]] =
            component["long_name"];
        }
      }
    }

    googleFormattedAddress.colony =
      googleFormattedAddress?.neighborhood ?? googleFormattedAddress.colony;

    onSelect({
      result,
      address,
      ...latLng,
    });
  };

  return (
    <PlacesAutocomplete
      value={value}
      onChange={onChange}
      onSelect={handleSelect}
      searchOptions={searchOptions}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <Container>
          <Input
            size="large"
            addonBefore={<AimOutlined style={{ color: "#52B266" }} />}
            {...props}
            {...getInputProps({
              placeholder,
            })}
            autoComplete="newpassword"
          />
          <DropDownContainer>
            {loading
              ? new Array(5).fill(0).map((_, index) => (
                <ResultItem key={index}>
                  <Skeleton
                    title={{ width: 100 }}
                    paragraph={{ rows: 1,
                      width: 200 }}
                    active
                    loading
                  />
                </ResultItem>
              ))
              : suggestions.map((suggestion: Suggestion) => (
                <ResultItem
                  active={suggestion.active}
                  {...getSuggestionItemProps(suggestion)}
                  key={suggestion.placeId} // Use a unique id from suggestion
                >
                  <Text>{suggestion.formattedSuggestion?.mainText}</Text>
                  <br />
                  <Text type="secondary">
                    {suggestion.formattedSuggestion?.secondaryText}
                  </Text>
                </ResultItem>
              ))}
          </DropDownContainer>
        </Container>
      )}
    </PlacesAutocomplete>
  );
};

export default LocationSearchInput;
